<template>
  <div>
    <a-page-header title="AI调用流程管理">
      <template #extra>
        <a-button type="primary" @click="handleCreate">
          <template #icon><PlusOutlined /></template>
          新建流程
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'is_default'">
            <a-tag v-if="record.is_default === 1" color="green">默认流程</a-tag>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="record.status === 1 ? 'green' : 'red'">
              {{ record.status === 1 ? '启用' : '禁用' }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button type="link" @click="handleEdit(record)">编辑</a-button>
            <a-button type="link" @click="handleManageSteps(record)">管理步骤</a-button>
            <a-popconfirm title="确定删除吗？" @confirm="handleDelete(record.id)">
              <a-button type="link" danger>删除</a-button>
            </a-popconfirm>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- AI调用流程编辑弹窗 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? '编辑AI调用流程' : '新建AI调用流程'"
      width="800px"
      @ok="handleSubmit"
      @cancel="handleCancel"
    >
      <a-form :model="formData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="流程名称" required>
          <a-input v-model:value="formData.scheme_name" placeholder="请输入流程名称" />
        </a-form-item>
        <a-form-item label="流程代码" required>
          <a-input v-model:value="formData.scheme_code" placeholder="请输入流程代码" />
        </a-form-item>
        <a-form-item label="流程描述">
          <a-textarea v-model:value="formData.description" :rows="3" placeholder="请输入流程描述" />
        </a-form-item>
        <a-form-item label="优先级">
          <a-input-number
            v-model:value="formData.priority"
            :min="0"
            :max="999"
            placeholder="数字越小优先级越高"
            style="width: 100%"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">数字越小优先级越高，匹配时优先选择</div>
        </a-form-item>
        <a-form-item label="适配Uid">
          <a-input
            v-model:value="formData.match_uid"
            placeholder="逗号分隔，如：uid1,uid2,uid3"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">精确匹配，多个值用逗号分隔</div>
        </a-form-item>
        <a-form-item label="适配uid末尾数字">
          <a-input
            v-model:value="formData.match_uid_suffix"
            placeholder="逗号分隔，如：0,1,2"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">匹配uid的最后一个字符，多个值用逗号分隔</div>
        </a-form-item>
        <a-form-item label="适配kvid">
          <a-input
            v-model:value="formData.match_kvid"
            placeholder="逗号分隔，如：kvid1,kvid2"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px">精确匹配，多个值用逗号分隔</div>
        </a-form-item>
        <a-form-item label="是否默认">
          <a-switch v-model:checked="formData.is_default" :checked-value="1" :unchecked-value="0" />
        </a-form-item>
        <a-form-item label="状态">
          <a-radio-group v-model:value="formData.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">禁用</a-radio>
          </a-radio-group>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- AI调用流程步骤管理弹窗（已废弃，现在使用独立页面） -->
    <a-modal
      v-model:open="stepModalVisible"
      title="流程步骤管理"
      width="900px"
      :footer="null"
      :destroy-on-close="true"
      :z-index="1000"
      @after-open="handleStepModalOpen"
    >
      <div v-if="currentScheme">
        <a-alert :message="`流程：${currentScheme.scheme_name}`" type="info" style="margin-bottom: 16px" />
        
        <a-table
          :columns="stepColumns"
          :data-source="schemeSteps"
          :loading="stepLoading"
          row-key="id"
          :pagination="false"
          :scroll="{ x: 'max-content' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'step_name'">
              <div>
                <div style="font-weight: 500;">{{ record.step_name }}</div>
                <div v-if="record.sub_steps && record.sub_steps.length > 0" style="margin-top: 8px; padding-left: 20px; color: #666">
                  <div v-for="sub in record.sub_steps" :key="sub.id" style="margin: 4px 0">
                    • {{ sub.step_name }} <a-tag size="small">({{ sub.knowledge_count || 0 }}个知识)</a-tag>
                  </div>
                </div>
                <!-- 显示同一 step_order 的其他步骤提示 -->
                <div v-if="getSameOrderStepCount(record) > 1" style="margin-top: 4px; color: #1890ff; font-size: 12px;">
                  同顺序还有 {{ getSameOrderStepCount(record) - 1 }} 个步骤
                </div>
              </div>
            </template>
            <template v-else-if="column.key === 'match_rules'">
              <div>
                <div v-if="record.match_uid">
                  <a-tag color="blue">UID: {{ record.match_uid }}</a-tag>
                </div>
                <div v-if="record.match_uid_suffix" style="margin-top: 4px">
                  <a-tag color="green">末尾: {{ record.match_uid_suffix }}</a-tag>
                </div>
                <div v-if="!record.match_uid && !record.match_uid_suffix" style="color: #999">
                  无匹配规则
                </div>
              </div>
            </template>
            <template v-else-if="column.key === 'is_default'">
              <a-tag :color="record.is_default === 1 ? 'red' : 'default'">
                {{ record.is_default === 1 ? '默认' : '-' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'is_required'">
              <a-tag :color="record.is_required === 1 ? 'red' : 'default'">
                {{ record.is_required === 1 ? '必选' : '可选' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'step_order'">
              <a-space>
                <a-button 
                  type="link" 
                  size="small" 
                  :disabled="record.step_order === 1"
                  @click="handleMoveStep(record, 'up')"
                >
                  ↑
                </a-button>
                <span>{{ record.step_order }}</span>
                <a-button 
                  type="link" 
                  size="small" 
                  :disabled="record.step_order === (schemeSteps?.length || 0)"
                  @click="handleMoveStep(record, 'down')"
                >
                  ↓
                </a-button>
              </a-space>
            </template>
            <template v-else-if="column.key === 'action'">
              <a-space>
                <a-button type="link" size="small" @click="handleEditStepForm(record)">编辑</a-button>
                <a-button 
                  v-if="record.is_default !== 1" 
                  type="link" 
                  size="small" 
                  @click="handleSetDefaultStep(record.id)"
                >
                  设默认
                </a-button>
                <a-button type="link" size="small" @click="handleEditStep(record)">
                  {{ record.is_required === 1 ? '设为可选' : '设为必选' }}
                </a-button>
                <a-popconfirm title="确定移除吗？" @confirm="handleRemoveStep(record.id)">
                  <a-button type="link" size="small" danger>移除</a-button>
                </a-popconfirm>
              </a-space>
            </template>
          </template>
        </a-table>

        <div style="margin-top: 16px">
          <a-button type="dashed" block @click="handleAddStep">
            <template #icon><PlusOutlined /></template>
            添加步骤
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- 添加/编辑步骤弹窗 -->
    <a-modal
      v-model:open="addStepModalVisible"
      :title="currentEditStepId ? '编辑步骤' : '添加步骤'"
      :z-index="2000"
      :mask-closable="false"
      @ok="handleAddStepSubmit"
      @cancel="handleCancelAddStep"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="选择步骤" required>
          <a-select
            v-model:value="addStepForm.call_step_id"
            placeholder="请选择大步骤"
            style="width: 100%"
            :disabled="!!currentEditStepId"
            :get-popup-container="(triggerNode) => {
              return triggerNode.parentElement?.closest('.ant-modal-wrap') || document.body;
            }"
          >
            <a-select-option v-for="step in availableSteps" :key="step.id" :value="step.id">
              {{ step.step_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="步骤顺序">
          <a-input-number v-model:value="addStepForm.step_order" :min="1" style="width: 100%" @change="handleStepOrderChange" />
        </a-form-item>
        <a-form-item label="是否默认">
          <a-switch v-model:checked="addStepForm.is_default" :checked-value="1" :unchecked-value="0" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">每个步骤顺序至少需要有一个默认步骤</div>
        </a-form-item>
        <a-form-item label="是否必选">
          <a-switch v-model:checked="addStepForm.is_required" :checked-value="1" :unchecked-value="0" />
        </a-form-item>
        <a-form-item label="匹配UID">
          <a-input v-model:value="addStepForm.match_uid" placeholder="逗号分隔，如：uid1,uid2,uid3" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">精确匹配UID，多个用逗号分隔</div>
        </a-form-item>
        <a-form-item label="匹配UID末尾">
          <a-input v-model:value="addStepForm.match_uid_suffix" placeholder="逗号分隔，如：0,1,2" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">匹配UID末尾数字，多个用逗号分隔</div>
        </a-form-item>
        <a-form-item label="优先级">
          <a-input-number v-model:value="addStepForm.priority" :min="0" style="width: 100%" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">数字越小优先级越高，0最高</div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import { getCallSchemes, createCallScheme, updateCallScheme, deleteCallScheme, getSchemeSteps, addSchemeStep, removeSchemeStep, updateSchemeStep, reorderSchemeSteps, setDefaultStep } from '../api/callScheme';
import { getCallSteps } from '../api/callStep';

export default {
  name: 'CallScheme',
  components: {
    PlusOutlined
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const dataSource = ref([]);
    const modalVisible = ref(false);
    const isEdit = ref(false);
    const currentId = ref(null);
    const stepModalVisible = ref(false);
    const stepLoading = ref(false);
    const schemeSteps = ref([]);
    const currentScheme = ref(null);
    const addStepModalVisible = ref(false);
    const availableSteps = ref([]);
    const currentEditStepId = ref(null);

    const formData = reactive({
      scheme_name: '',
      scheme_code: '',
      description: '',
      priority: 0,
      match_uid: '',
      match_uid_suffix: '',
      match_kvid: '',
      is_default: 0,
      status: 1
    });

    const addStepForm = reactive({
      call_step_id: undefined,
      step_order: 1,
      is_default: 1,
      is_required: 1,
      match_uid: '',
      match_uid_suffix: '',
      priority: 0
    });

    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: '流程名称', dataIndex: 'scheme_name', key: 'scheme_name' },
      { title: '流程代码', dataIndex: 'scheme_code', key: 'scheme_code', width: 150 },
      { title: '默认流程', key: 'is_default', width: 100 },
      { title: '状态', key: 'status', width: 100 },
      { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 180 },
      { title: '操作', key: 'action', width: 250, fixed: 'right' }
    ];

    const stepColumns = [
      { title: '顺序', dataIndex: 'step_order', key: 'step_order', width: 100 },
      { title: '步骤名称', key: 'step_name' },
      { title: '匹配规则', key: 'match_rules', width: 200 },
      { title: '默认', key: 'is_default', width: 80 },
      { title: '优先级', dataIndex: 'priority', key: 'priority', width: 80 },
      { title: '是否必选', key: 'is_required', width: 100 },
      { title: '操作', key: 'action', width: 250 }
    ];

    const loadData = async () => {
      loading.value = true;
      try {
        const res = await getCallSchemes();
        const data = res.data || [];
        // 按 id 正序排列
        dataSource.value = data.sort((a, b) => a.id - b.id);
      } catch (error) {
        message.error('加载数据失败');
      } finally {
        loading.value = false;
      }
    };

    const loadAvailableSteps = async () => {
      try {
        const res = await getCallSteps();
        // 只显示大步骤（level=1）
        availableSteps.value = (res.data || []).filter(s => s.level === 1);
      } catch (error) {
        console.error('加载步骤失败:', error);
      }
    };

    const loadSchemeSteps = async (schemeId) => {
      stepLoading.value = true;
      try {
        const res = await getSchemeSteps(schemeId);
        console.log('加载流程步骤完整响应:', JSON.stringify(res, null, 2));
        
        // 响应拦截器返回的是 { code, message, data }
        // data 的结构是 { scheme: {...}, steps: [...] }
        let stepsData = [];
        
        if (res && res.data) {
          if (res.data.steps && Array.isArray(res.data.steps)) {
            // 标准格式：{ scheme: {...}, steps: [...] }
            stepsData = res.data.steps;
          } else if (Array.isArray(res.data)) {
            // 如果 data 直接是数组
            stepsData = res.data;
          }
        }
        
        schemeSteps.value = stepsData;
        console.log('最终设置的流程步骤数据:', schemeSteps.value);
        console.log('步骤数量:', schemeSteps.value.length);
        
        if (stepsData.length === 0) {
          console.warn('警告：步骤列表为空，请检查数据库是否有数据');
        }
      } catch (error) {
        console.error('加载步骤失败:', error);
        console.error('错误详情:', error.response?.data || error.message);
        message.error('加载步骤失败: ' + (error.response?.data?.message || error.message));
      } finally {
        stepLoading.value = false;
      }
    };

    // 生成随机4个小写字符
    const generateRandomCode = () => {
      const chars = 'abcdefghijklmnopqrstuvwxyz';
      let result = '';
      for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return `scheme-${result}`;
    };

    const handleCreate = () => {
      isEdit.value = false;
      currentId.value = null;
      Object.assign(formData, {
        scheme_name: '',
        scheme_code: generateRandomCode(),
        description: '',
        priority: 0,
        match_uid: '',
        match_uid_suffix: '',
        match_kvid: '',
        is_default: 0,
        status: 1
      });
      modalVisible.value = true;
    };

    const handleEdit = async (record) => {
      isEdit.value = true;
      currentId.value = record.id;
      Object.assign(formData, {
        scheme_name: record.scheme_name,
        scheme_code: record.scheme_code,
        description: record.description || '',
        priority: record.priority || 0,
        match_uid: record.match_uid || '',
        match_uid_suffix: record.match_uid_suffix || '',
        match_kvid: record.match_kvid || '',
        is_default: record.is_default,
        status: record.status
      });
      modalVisible.value = true;
    };

    const handleDelete = async (id) => {
      try {
        await deleteCallScheme(id);
        message.success('删除成功');
        loadData();
      } catch (error) {
        message.error('删除失败');
      }
    };

    const handleSubmit = async () => {
      try {
        if (isEdit.value) {
          await updateCallScheme(currentId.value, formData);
          message.success('更新成功');
        } else {
          await createCallScheme(formData);
          message.success('创建成功');
        }
        modalVisible.value = false;
        loadData();
      } catch (error) {
        message.error(isEdit.value ? '更新失败' : '创建失败');
      }
    };

    const handleCancel = () => {
      modalVisible.value = false;
    };

    const handleManageSteps = (record) => {
      // 跳转到步骤管理独立页面
      router.push({ name: 'call-step-manage', params: { schemeId: record.id } });
    };

    const handleStepModalOpen = async () => {
      // 弹窗打开后的回调，如果数据还没加载，再次尝试加载
      if (currentScheme.value && (!schemeSteps.value || schemeSteps.value.length === 0)) {
        await loadSchemeSteps(currentScheme.value.id);
      }
    };

    const handleAddStep = async () => {
      await loadAvailableSteps();
      // 过滤掉已添加的步骤
      const existingStepIds = schemeSteps.value.map(s => s.call_step_id);
      availableSteps.value = availableSteps.value.filter(s => !existingStepIds.includes(s.id));
      
      console.log('可用步骤列表:', availableSteps.value);
      
      // 计算最大 step_order
      const maxStepOrder = schemeSteps.value.length > 0 
        ? Math.max(...schemeSteps.value.map(s => s.step_order))
        : 0;
      const defaultStepOrder = maxStepOrder + 1;
      
      // 检查该 step_order 是否已有步骤
      const existingStepsForOrder = schemeSteps.value.filter(s => s.step_order === defaultStepOrder);
      const defaultIsDefault = existingStepsForOrder.length === 0 ? 1 : 0;
      
      Object.assign(addStepForm, {
        call_step_id: undefined,
        step_order: defaultStepOrder,
        is_default: defaultIsDefault,
        is_required: 1,
        match_uid: '',
        match_uid_suffix: '',
        priority: 0
      });
      // 使用 nextTick 确保弹窗正确显示在最上层
      await nextTick();
      addStepModalVisible.value = true;
    };

    // 当用户修改步骤顺序时，自动更新是否默认
    const handleStepOrderChange = (value) => {
      if (!value || !currentScheme.value) return;
      
      // 检查该 step_order 是否已有步骤（编辑模式下排除当前正在编辑的步骤）
      const existingStepsForOrder = schemeSteps.value.filter(s => {
        if (currentEditStepId.value && s.id === currentEditStepId.value) {
          return false; // 排除当前正在编辑的步骤
        }
        return s.step_order === value;
      });
      
      // 如果该 step_order 还没有步骤，且当前 is_default 为 0，则自动设置为 1
      // 如果该 step_order 已有步骤，且当前 is_default 为 1，则自动设置为 0
      if (existingStepsForOrder.length === 0) {
        // 该 step_order 还没有步骤，应该设置为默认
        if (addStepForm.is_default === 0) {
          addStepForm.is_default = 1;
          message.info('该步骤顺序还没有步骤，已自动设置为默认');
        }
      } else {
        // 该 step_order 已有步骤，检查是否已有默认步骤
        const hasDefault = existingStepsForOrder.some(s => s.is_default === 1);
        if (hasDefault && addStepForm.is_default === 1) {
          // 如果已有默认步骤，且当前设置为默认，提示用户
          message.warning('该步骤顺序已有默认步骤，建议设置为非默认');
        } else if (!hasDefault && addStepForm.is_default === 0) {
          // 如果没有默认步骤，且当前设置为非默认，自动设置为默认
          addStepForm.is_default = 1;
          message.info('该步骤顺序还没有默认步骤，已自动设置为默认');
        }
      }
    };

    const handleAddStepSubmit = async () => {
      if (!addStepForm.call_step_id) {
        message.warning('请选择步骤');
        return;
      }
      try {
        if (currentEditStepId.value) {
          // 编辑步骤
          await updateSchemeStep(currentEditStepId.value, {
            step_order: addStepForm.step_order,
            is_required: addStepForm.is_required,
            remark: '',
            match_uid: addStepForm.match_uid || null,
            match_uid_suffix: addStepForm.match_uid_suffix || null,
            priority: addStepForm.priority || 0,
            is_default: addStepForm.is_default
          });
          message.success('更新成功');
        } else {
          // 添加步骤
          await addSchemeStep(currentScheme.value.id, {
            ...addStepForm,
            is_default: addStepForm.is_default
          });
          message.success('添加成功');
        }
        addStepModalVisible.value = false;
        currentEditStepId.value = null;
        // 重新加载步骤列表
        await loadSchemeSteps(currentScheme.value.id);
      } catch (error) {
        console.error('操作失败:', error);
        message.error((currentEditStepId.value ? '更新' : '添加') + '失败: ' + (error.response?.data?.message || error.message));
      }
    };

    const handleCancelAddStep = () => {
      addStepModalVisible.value = false;
      currentEditStepId.value = null;
      Object.assign(addStepForm, {
        call_step_id: undefined,
        step_order: 1,
        is_default: 1,
        is_required: 1,
        match_uid: '',
        match_uid_suffix: '',
        priority: 0
      });
    };

    const handleRemoveStep = async (stepId) => {
      try {
        await removeSchemeStep(stepId);
        message.success('移除成功');
        await loadSchemeSteps(currentScheme.value.id);
      } catch (error) {
        message.error('移除失败');
      }
    };

    const handleEditStep = async (record) => {
      // 编辑步骤（修改必选/可选）
      const newRequired = record.is_required === 1 ? 0 : 1;
      try {
        await updateSchemeStep(record.id, {
          step_order: record.step_order,
          is_required: newRequired,
          remark: record.remark
        });
        message.success('更新成功');
        await loadSchemeSteps(currentScheme.value.id);
      } catch (error) {
        message.error('更新失败');
      }
    };

    const handleEditStepForm = (record) => {
      // 打开编辑步骤表单
      Object.assign(addStepForm, {
        call_step_id: record.call_step_id,
        step_order: record.step_order,
        is_default: record.is_default || 0,
        is_required: record.is_required,
        match_uid: record.match_uid || '',
        match_uid_suffix: record.match_uid_suffix || '',
        priority: record.priority || 0
      });
      currentEditStepId.value = record.id;
      addStepModalVisible.value = true;
    };

    const handleSetDefaultStep = async (stepId) => {
      try {
        await setDefaultStep(stepId);
        message.success('设置默认步骤成功');
        await loadSchemeSteps(currentScheme.value.id);
      } catch (error) {
        message.error('设置默认步骤失败: ' + (error.response?.data?.message || error.message));
      }
    };

    const handleMoveStep = async (record, direction) => {
      const currentIndex = schemeSteps.value.findIndex(s => s.id === record.id);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= schemeSteps.value.length) return;

      // 交换两个步骤的顺序值
      const targetStep = schemeSteps.value[newIndex];
      const currentOrder = record.step_order;
      const targetOrder = targetStep.step_order;

      // 更新到服务器
      const stepsToUpdate = [
        { id: record.id, step_order: targetOrder },
        { id: targetStep.id, step_order: currentOrder }
      ];

      try {
        await reorderSchemeSteps(currentScheme.value.id, stepsToUpdate);
        message.success('顺序更新成功');
        await loadSchemeSteps(currentScheme.value.id);
      } catch (error) {
        message.error('更新顺序失败');
      }
    };

    // 获取同一 step_order 的步骤数量
    const getSameOrderStepCount = (record) => {
      if (!schemeSteps.value || !record) return 1;
      return schemeSteps.value.filter(s => s.step_order === record.step_order).length;
    };


    onMounted(() => {
      loadData();
    });

      return {
        loading,
        dataSource,
        modalVisible,
        isEdit,
        formData,
        columns,
        handleCreate,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleCancel,
        stepModalVisible,
        stepLoading,
        schemeSteps,
        currentScheme,
        stepColumns,
        handleManageSteps,
        addStepModalVisible,
        availableSteps,
        addStepForm,
        handleAddStep,
        handleAddStepSubmit,
        handleRemoveStep,
        handleEditStep,
        handleEditStepForm,
        handleSetDefaultStep,
        handleMoveStep,
        handleStepModalOpen,
        handleCancelAddStep,
        getSameOrderStepCount,
        currentEditStepId,
        handleStepOrderChange
      };
  }
};
</script>

